import { useEffect, useState } from "react";
import { Spinner, useToast } from "@chakra-ui/react";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import {
  CreateUpdateInvoiceInput,
  Invoice,
  useCreateInvoiceMutation,
  useGetInvoiceLazyQuery,
  useUpdateInvoiceMutation,
} from "../../generated/graphql";
import { useErrorMessage } from "../../hooks/errorMessage";
import {
  MCFormInvoiceDetail,
  MCFormInvoiceItems,
  IMCFormInvoiceDetail,
} from "../../components/MCFormInvoice";
import {
  FormMutationType,
  InheritFullWidthModalProps,
  SaveType,
} from "../../components/FullWidthModal/interface";
import FullWidthModal from "../../components/FullWidthModal";
import { IMCFormInvoiceItems } from "../../components/MCFormInvoice";
import moment from "moment";

const navigations = ["Detail Invoice", "Detail Item"];

const InvoiceForm: React.FC<InheritFullWidthModalProps<Invoice>> = ({
  id,
  onClose,
  isOpen,
  ...props
}) => {
  const [getInvoice, { data, loading: isLoadingData }] =
    useGetInvoiceLazyQuery();

  useEffect(() => {
    if (id && isOpen) {
      getInvoice({ variables: { id } });
    }
  }, [id, isOpen, getInvoice]);

  const [detailValue, setDetailValue] = useState<IMCFormInvoiceDetail>();
  const [itemsValue, setItemsValue] = useState<IMCFormInvoiceItems>();

  useEffect(() => {
    if (data) {
      const {
        invoiceDate,
        customerName,
        customerSignatureName,
        refNo,
        bookerName,
        paymentType,
        additionalCost,
        invoiceItems,
        paymentDetailId,
        paymentDetail,
        signatureId,
        signature,
        signatureBackgroundId,
        signatureBackground,
      } = data?.getInvoice as Invoice;

      setDetailValue({
        customerName: customerName || "",
        customerSignatureName: customerSignatureName || "",
        paymentType: paymentType,
        refNo: refNo || "",
        bookerName: bookerName || "",
        invoiceDate: moment(invoiceDate).format("Y-MM-DD"),
        paymentDetail: paymentDetailId ? paymentDetail : null,
        signature: signatureId ? signature : null,
        signatureBackground: signatureBackgroundId ? signatureBackground : null,
      });

      const updateInvoiceItems = invoiceItems.map(
        ({ __typename, ...restInvoiceItems }) => {
          return restInvoiceItems;
        }
      );

      setItemsValue({
        additionalCost: additionalCost || 0,
        items: updateInvoiceItems,
      });
    }
  }, [data]);

  const { confirmation, setIsLoading, close } = useConfirmation();

  const toast = useToast();

  const { showErrorMessage } = useErrorMessage();
  const [createInvoiceMutation] = useCreateInvoiceMutation();
  const [updateInvoiceMutation] = useUpdateInvoiceMutation();

  const [screenIndex, setScreenIndex] = useState(0);

  const reset = () => {
    setScreenIndex(0);
    setDetailValue(undefined);
    setItemsValue(undefined);
  };

  const saveInvoice = async ({
    paymentDetail,
    signature,
    signatureBackground,
    ...data
  }: IMCFormInvoiceDetail & IMCFormInvoiceItems) => {
    try {
      setIsLoading(true);

      const input: CreateUpdateInvoiceInput = {
        ...data,
        invoiceDate: moment(data.invoiceDate).format("Y-MM-DD 00:00:00"),
        additionalCost: data.additionalCost || undefined,
        paymentDetailId: paymentDetail?.id || null,
        signatureId: signature?.id || null,
        signatureBackgroundId: signatureBackground?.id || null,
        items: data.items.map(({ ...elm }) => ({
          ...elm,
          price: parseFloat(elm.price.toString()),
          qty: parseInt(elm.qty.toString()),
        })),
      };

      if (id) {
        const res = await updateInvoiceMutation({
          variables: {
            id,
            input,
          },
        });
        toast({
          description: "Invoice successfully updated!",
          status: "success",
        });
        onClose({
          type: FormMutationType.UPDATE,
          data: res.data?.updateInvoice && (res.data?.updateInvoice as Invoice),
        });
      } else {
        await createInvoiceMutation({
          variables: {
            input,
          },
        });
        toast({
          description: "Invoice successfully created!",
          status: "success",
        });
        onClose({ type: FormMutationType.CREATE });
      }

      close();
      reset();
    } catch (e) {
      showErrorMessage(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FullWidthModal
      title={id ? "Edit Invoice" : "New Invoice"}
      onClose={() => {
        reset();
        onClose();
      }}
      isOpen={isOpen}
      {...props}
    >
      {isLoadingData ? (
        <Spinner />
      ) : (
        <>
          {screenIndex === 1 ? (
            <MCFormInvoiceItems
              value={itemsValue}
              navigations={navigations.map((elm, index) => ({
                label: elm,
                isActive: index === 1,
              }))}
              onBack={(data) => {
                setItemsValue(data);
                setScreenIndex((index) => index - 1);
              }}
              onSubmit={async (value, type) => {
                confirmation({
                  description: "Are you sure want to save?",
                  callback: () => {
                    saveInvoice({
                      ...(detailValue as IMCFormInvoiceDetail),
                      ...value,
                    });
                  },
                });
              }}
            />
          ) : (
            <MCFormInvoiceDetail
              navigations={navigations.map((elm, index) => ({
                label: elm,
                isActive: index === 0,
              }))}
              value={detailValue}
              onBack={() => {
                onClose();
              }}
              onSubmit={async (value, type) => {
                if (type === SaveType.DRAFT) {
                  console.log("Save to draft");
                } else {
                  setDetailValue(value);
                  setScreenIndex((i) => i + 1);
                }
              }}
            />
          )}
        </>
      )}
    </FullWidthModal>
  );
};

export default InvoiceForm;
